import React from "react";
import { TacticIcon } from "@cmp-src/icons/TacticLogo/TacticIcon";
import { cn } from "@gettactic/helpers";

/** Max allowed aspect ratio deviation from 1:1 (e.g. 2.5 means up to 2.5:1 or 1:2.5) */
const MAX_ASPECT_RATIO = 2.5;

interface OrgLogoProps {
  /** The organization's logo; the Tactic symbol stands in without one. */
  logoUrl: string | null | undefined;
  /** Names the logo (and the symbol standing in for it). */
  alt?: string;
  /** Classes applied to the <img> element */
  className?: string;
  /** Classes applied to the TacticIcon fallback (replaces className when provided) */
  fallbackClassName?: string;
}

/**
 * An organization's logo, or the Tactic symbol when there is none, it fails to load or it is
 * too far from square (beyond 2.5:1) to fit the logo slots.
 */
export const OrgLogo: React.FC<OrgLogoProps> = ({
  logoUrl,
  alt = "Organization logo",
  className,
  fallbackClassName
}) => {
  const [showLogo, setShowLogo] = React.useState<boolean | null>(null);

  // Reset state when URL changes
  React.useEffect(() => {
    setShowLogo(null);
  }, [logoUrl]);

  const iconClassName = fallbackClassName ?? className;

  if (!logoUrl || showLogo === false) {
    // Stands in for the logo, so it carries its name
    return (
      <TacticIcon role="img" aria-label={alt} className={cn(iconClassName)} />
    );
  }

  return (
    <>
      <img
        src={logoUrl}
        alt={alt}
        className={cn(className, showLogo === null && "invisible absolute")}
        onLoad={(e) => {
          const img = e.currentTarget;
          const ratio = img.naturalWidth / img.naturalHeight;
          setShowLogo(
            ratio >= 1 / MAX_ASPECT_RATIO && ratio <= MAX_ASPECT_RATIO
          );
        }}
        onError={() => setShowLogo(false)}
      />
      {showLogo === null && (
        <TacticIcon aria-hidden className={cn(iconClassName)} />
      )}
    </>
  );
};
